import { useEffect, useRef } from "react";
import { Switch, Route, useLocation } from "wouter";
import * as Sentry from "@sentry/react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { UserDropdown } from "@/components/user-dropdown";
import NotFound from "@/pages/not-found";
import SearchPage from "@/pages/search";
import BatchImportPage from "@/pages/batch-import";
import AlertsPage from "@/pages/alerts";
import TransactionDetailsPage from "@/pages/transaction-details";
import BlockdaemonTestPage from "@/pages/blockdaemon-test";
import ProfilePage from "@/pages/profile";
import MFASetupPage from "@/pages/mfa-setup";
import LoginPage from "@/pages/login";
import SignupPage from "@/pages/signup";
import VerifyEmailPage from "@/pages/verify-email";
import UnsubscribePage from "@/pages/unsubscribe";
import ForgotPasswordPage from "@/pages/forgot-password";
import ResetPasswordPage from "@/pages/reset-password";
import AdminPage from "@/pages/admin";
import LegalPage from "@/pages/legal";
import { ProtectedRoute } from "./lib/protected-route";
import { AuthProvider } from "./hooks/use-auth";
import "react-day-picker/style.css";
import SearchLandingPage from "@/pages/search";
import AddressSearchPage from "@/pages/search-address";
import TransactionSearchPage from "@/pages/search-transaction";
import BlockHashSearchPage from "@/pages/search-block-hash";
import BlockHeightSearchPage from "@/pages/search-block-height";
import FavouritesPage from "@/pages/favourites";
import FeedbackPage from "@/pages/feedback";
import Sidebar from "./components/sidebar";
import { BackButton } from "./components/back-button";
import { UsageLimitExceededDialog } from "./components/UsageLimitExceededDialog";
import { SessionExpiredDialog } from "./components/SessionExpiredDialog";
import { handleSessionExpired } from "./stores/sessionExpiredStore";

function LoggedInLayout({ workspace }: { workspace: React.ReactNode }) {
 
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-64">
        <nav className="bg-background px-4 py-3 h-[60px]">
          <div className="flex justify-between items-center">
            <BackButton />
            <div className="flex items-center gap-4">
              <UserDropdown />
            </div>
          </div>
        </nav>
        <div className="flex-1 bg-background">
          {workspace}
        </div>
      </div>
    </div>
  )
}


function Router() {
  return (
    <Switch>
      <ProtectedRoute path="/admin" component={() => <LoggedInLayout workspace={<AdminPage />} />} />
      <ProtectedRoute path="/legal" component={() => <LoggedInLayout workspace={<LegalPage />} />} />
      <ProtectedRoute path="/search/address" component={() => <LoggedInLayout workspace={<AddressSearchPage />} />} />
      <ProtectedRoute path="/search/transaction" component={() => <LoggedInLayout workspace={<TransactionSearchPage />} />} />
      <ProtectedRoute path="/search/block-hash" component={() => <LoggedInLayout workspace={<BlockHashSearchPage />} />} />
      <ProtectedRoute path="/search/block-height" component={() => <LoggedInLayout workspace={<BlockHeightSearchPage />} />} />
      <ProtectedRoute path="/search" component={() => <LoggedInLayout workspace={<SearchLandingPage />} />} />
      <ProtectedRoute path="/batch-import" component={() => <LoggedInLayout workspace={<BatchImportPage />} />} />
      <ProtectedRoute path="/favorites" component={() => <LoggedInLayout workspace={<FavouritesPage />} />} />
      <ProtectedRoute path="/favourites" component={() => <LoggedInLayout workspace={<FavouritesPage />} />} />
      <ProtectedRoute path="/feedback" component={() => <LoggedInLayout workspace={<FeedbackPage />} />} />
      <ProtectedRoute path="/alerts" component={() => <LoggedInLayout workspace={<AlertsPage />} />} />
      <ProtectedRoute path="/tx/:txid" component={() => <LoggedInLayout workspace={<TransactionDetailsPage />} />} />
      <ProtectedRoute path="/transaction/:txid" component={() => <LoggedInLayout workspace={<TransactionDetailsPage />} />} />
      <ProtectedRoute path="/profile" component={() => <LoggedInLayout workspace={<ProfilePage />} />} />
      <ProtectedRoute path="/account" component={() => <LoggedInLayout workspace={<ProfilePage />} />} />
      <ProtectedRoute path="/account/mfa-setup" component={() => <LoggedInLayout workspace={<MFASetupPage />} />} />
      <Route path="/login" component={LoginPage} />
      <Route path="/signup" component={SignupPage} />
      <Route path="/verify-email" component={VerifyEmailPage} />
      <Route path="/unsubscribe" component={UnsubscribePage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/blockdaemon-test" component={BlockdaemonTestPage} />
      <ProtectedRoute path="/"  component={() => <LoggedInLayout workspace={<SearchLandingPage />} />} />
      <Route component={NotFound} />
    </Switch>
  );
}

const getFetchUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
};

const originalFetch = window.fetch.bind(window);
window.fetch = async (...args) => {
  const res = await originalFetch(...args);
  if (res.status === 401) {
    const url = getFetchUrl(args[0]);
    if (!url.includes("/api/login") && !url.includes("/api/mfa/verify")) {
      handleSessionExpired();
    }
  }
  return res;
};

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-2xl font-bold mb-4 text-destructive">Something went wrong</h1>
            <p className="text-muted-foreground mb-6">
              An unexpected error occurred. Please try again.
            </p>
            <button
              onClick={resetError}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Try again
            </button>
          </div>
        </div>
      )}
      showDialog={false}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="system" storageKey="blockseer-theme">
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Router />
              <UsageLimitExceededDialog />
              <SessionExpiredDialog />
            </TooltipProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;